import { useState, useEffect, useMemo, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { STATUS_META, ROLE_META } from '../utils/constants';
import { Icon } from '../components/common/Icon';
import { StatusBadge } from '../components/common/Badge';
import { deleteRestaurant, archiveRestaurant, bulkArchiveRestaurants, touchInteraction, releaseExpiredClaims, checkPendingFeedback, submitFeedback, hasPendingFeedback, FEEDBACK_REASONS, FEEDBACK_HOURS_THRESHOLD, flagIdleLeadsForFeedback } from '../services/restaurantService';

/**
 * Contacts Page - Restaurant List View
 * Displays restaurants with filtering, search, and actions
 */
export default function ContactsPage({ viewMode = 'all' }) {
  const ctx = useContext(AppContext);
  const { 
    restaurants, 
    users, 
    currentUser, 
    setModal,
    selectedIds,
    setSelectedIds,
    settings 
  } = ctx;
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState(null);
  const [areaFilter, setAreaFilter] = useState('all');
  const [marketerFilter, setMarketerFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [reassignModal, setReassignModal] = useState(null);
  const [showMarketerReassign, setShowMarketerReassign] = useState(false);
  const [reassignTargetMarketer, setReassignTargetMarketer] = useState('');
  const [feedbackModal, setFeedbackModal] = useState(null); // Array of pending feedback items
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const role = currentUser.role;

  // Delete single restaurant
  const handleDeleteRestaurant = async (restaurant) => {
    if (!confirm(`هل أنت متأكد من حذف "${restaurant.name}"؟

هذا الإجراء لا يمكن التراجع عنه!`)) {
      return;
    }

    setDeleting(true);
    try {
      const success = await deleteRestaurant(restaurant.id);
      if (success) {
        showToast('✅ تم حذف المطعم بنجاح');
        // Reload restaurants
        if (ctx.loadRestaurants) {
          ctx.loadRestaurants();
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      showToast('❌ فشل في حذف المطعم: ' + err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Delete selected restaurants (bulk delete)
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showToast('⚠️ يرجى اختيار مطاعم للحذف', 'warning');
      return;
    }

    if (!confirm(`هل أنت متأكد من حذف ${selectedIds.length} مطعم؟

هذا الإجراء لا يمكن التراجع عنه!`)) {
      return;
    }

    setDeleting(true);
    try {
      let deletedCount = 0;
      let errorCount = 0;

      for (const id of selectedIds) {
        try {
          const success = await deleteRestaurant(id);
          if (success) deletedCount++;
        } catch (err) {
          console.error(`Failed to delete ${id}:`, err);
          errorCount++;
        }
      }

      if (deletedCount > 0) {
        showToast(`✅ تم حذف ${deletedCount} مطعم بنجاح`);
        setSelectedIds([]);
        if (ctx.loadRestaurants) {
          ctx.loadRestaurants();
        }
      }
      
      if (errorCount > 0) {
        showToast(`⚠️ فشل في حذف ${errorCount} مطعم`, 'warning');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      showToast('❌ فشل في الحذف الجماعي', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Archive single restaurant
  const handleArchiveRestaurant = async (restaurant) => {
    // Auto-archive if deal_closed
    if (restaurant.status === 'deal_closed') {
      if (!confirm(`✅ "${restaurant.name}" - صفقة مغلقة!

هل تريد أرشفة هذا المطعم؟`)) {
        return;
      }

      setArchiving(true);
      try {
        const success = await archiveRestaurant(restaurant.id, true);
        if (success) {
          showToast('✅ تم أرشفة الصفقة المغلقة بنجاح');
          if (ctx.loadRestaurants) {
            ctx.loadRestaurants();
          }
        }
      } catch (err) {
        console.error('Archive error:', err);
        showToast('❌ فشل في أرشفة المطعم: ' + err.message, 'error');
      } finally {
        setArchiving(false);
      }
    } else {
      // Manual archive (not interested)
      if (!confirm(`هل تريد أرشفة "${restaurant.name}" كـ "غير مهتم"؟

سيتم نقله إلى الأرشيف ولن يظهر في القائمة الرئيسية.`)) {
        return;
      }

      setArchiving(true);
      try {
        // Update status to 'archived' before archiving
        const { updateRestaurant } = ctx;
        if (updateRestaurant) {
          await updateRestaurant(restaurant.id, { status: 'archived' });
        }
        
        const success = await archiveRestaurant(restaurant.id, true);
        if (success) {
          showToast('✅ تم أرشفة المطعم كـ "غير مهتم"');
          if (ctx.loadRestaurants) {
            ctx.loadRestaurants();
          }
        }
      } catch (err) {
        console.error('Archive error:', err);
        showToast('❌ فشل في أرشفة المطعم: ' + err.message, 'error');
      } finally {
        setArchiving(false);
      }
    }
  };

  // Bulk archive selected restaurants
  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) {
      showToast('⚠️ يرجى اختيار مطاعم للأرشفة', 'warning');
      return;
    }

    if (!confirm(`هل تريد أرشفة ${selectedIds.length} مطعم؟

سيتم نقلها إلى الأرشيف ولن تظهر في القائمة الرئيسية.`)) {
      return;
    }

    setArchiving(true);
    try {
      const result = await bulkArchiveRestaurants(selectedIds, true);
      
      if (result.success > 0) {
        showToast(`✅ تم أرشفة ${result.success} مطعم بنجاح`);
        setSelectedIds([]);
        if (ctx.loadRestaurants) {
          ctx.loadRestaurants();
        }
      }
      
      if (result.failed > 0) {
        showToast(`⚠️ فشل في أرشفة ${result.failed} مطعم`, 'warning');
      }
    } catch (err) {
      console.error('Bulk archive error:', err);
      showToast('❌ فشل في الأرشفة الجماعية', 'error');
    } finally {
      setArchiving(false);
    }
  };

  // Role-based filtering
  const visibleRestaurants = useMemo(() => {
    let rs = restaurants || [];
    
    // Apply view mode filter first
    if (viewMode === 'my-contacts') {
      // Show only contacts assigned to current user
      rs = rs.filter(r => r.assignedTo === currentUser.id);
    } else if (role === 'marketer') {
      // Marketers see: assigned to them OR unassigned
      rs = rs.filter(r => {
        // Show if assigned to me
        if (r.assignedTo === currentUser.id) return true;
        // Show if unassigned
        if (!r.assignedTo) return true;
        return false;
      });
    } else if (role === 'data_entry') {
      rs = rs.filter(r => r.status === 'data_review');
    } else if (role === 'accountant') {
      rs = rs.filter(r => r.status === 'invoice_pending');
    } else if (role === 'rep') {
      // Reps see: assigned to them OR unassigned (ready_for_rep status)
      rs = rs.filter(r => r.assignedRep === currentUser.id || r.status === 'ready_for_rep');
    }
    
    // Admin/Marketer assignment filter
    if ((role === 'admin' || role === 'sales_manager' || role === 'marketer') && assignmentFilter !== 'all') {
      if (assignmentFilter === 'unassigned') {
        rs = rs.filter(r => !r.assignedTo);
      } else if (assignmentFilter === 'assigned') {
        rs = rs.filter(r => r.assignedTo);
      }
    }
    
    // Area filter
    if (areaFilter !== 'all') {
      rs = rs.filter(r => r.area === areaFilter);
    }
    
    // Apply user filter from "Show Work" button
    // Apply marketer filter (admin/sales_manager only)
    if (marketerFilter !== 'all' && (role === 'admin' || role === 'sales_manager')) {
      if (marketerFilter === 'unassigned') {
        rs = rs.filter(r => !r.assignedTo);
      } else {
        rs = rs.filter(r => r.assignedTo === marketerFilter);
      }
    }
    
    if (userFilter && (role === 'admin' || role === 'sales_manager')) {
      if (userFilter.role === 'marketer') {
        rs = rs.filter(r => r.assignedTo === userFilter.userId);
      } else if (userFilter.role === 'rep') {
        rs = rs.filter(r => r.assignedRep === userFilter.userId);
      }
    }
    
    return rs;
  }, [restaurants, role, currentUser.id, userFilter, marketerFilter, assignmentFilter, areaFilter, viewMode, sortBy, pageSize]);

  // Extract unique areas for filter
  const uniqueAreas = useMemo(() => {
    const areas = new Set(restaurants?.map(r => r.area).filter(Boolean));
    return Array.from(areas).sort();
  }, [restaurants]);

  // Apply search and status filters
  const filtered = useMemo(() => {
    let result = visibleRestaurants.filter(r => {
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      const q = search.toLowerCase();
      const matchSearch = !q || 
        r.name?.toLowerCase().includes(q) || 
        r.ownerName?.toLowerCase().includes(q) || 
        r.phone?.includes(q) || 
        r.area?.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
    
    // Sort results
    if (sortBy === 'name-asc') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ar'));
    } else if (sortBy === 'name-desc') {
      result.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'ar'));
    } else if (sortBy === 'date-newest') {
      result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === 'date-oldest') {
      result.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    }
    
    return result;
  }, [visibleRestaurants, statusFilter, search, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedRestaurants = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, areaFilter, marketerFilter, assignmentFilter, userFilter, pageSize]);

  // Release expired claims on page load
  useEffect(() => {
    releaseExpiredClaims().then(count => {
      if (count > 0) {
        ctx.loadRestaurants?.();
      }
    });
    
    // Flag idle leads for feedback (client-side alternative to pg_cron)
    flagIdleLeadsForFeedback().then(count => {
      if (count > 0) {
        ctx.showToast?.(`⚠️ تم اكتشاف ${count} مطعم/مطاعم متأخرة - مطلوب تقديم سبب`);
      }
    });
    
    // Check for pending feedback requirements
    checkPendingFeedback(currentUser.id).then(pending => {
      if (pending.length > 0) {
        setFeedbackModal(pending);
      }
    });
  }, []);

  const allSelected = paginatedRestaurants.length > 0 && paginatedRestaurants.every(r => selectedIds.includes(r.id));
  const toggleAll = () => allSelected ? setSelectedIds([]) : setSelectedIds(paginatedRestaurants.map(r => r.id));
  const toggleOne = id => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  // Status counts
  const statusCounts = Object.keys(STATUS_META).reduce((acc, k) => ({
    ...acc, 
    [k]: visibleRestaurants.filter(r => r.status === k).length
  }), {});

  const canImport = role === 'admin';
  const canAdd = role === 'admin' || role === 'marketer';

  // Calculate daily target progress (admin only)
  const todayTarget = 10;
  const todayCount = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return (restaurants || []).filter(r => {
      const createdDate = r.createdAt?.split('T')[0];
      return createdDate === today;
    }).length;
  }, [restaurants]);
  const targetProgress = Math.min((todayCount / todayTarget) * 100, 100);

  // Styles
  const S = {
    input: { width: '100%', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', color: '#0f172a', fontSize: 13, fontFamily: "'Cairo',sans-serif", outline: 'none', boxSizing: 'border-box', display: 'block' },
    th: { padding: '11px 14px', color: '#64748b', fontSize: 11, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' },
    btnPrimary: { background: 'linear-gradient(135deg,#4ecdc4,#38b2ac)', border: 'none', borderRadius: 10, padding: '10px 18px', color: '#fff', cursor: 'pointer', fontFamily: "'Cairo',sans-serif", fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },
    btnSec: { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 18px', color: '#64748b', cursor: 'pointer', fontFamily: "'Cairo',sans-serif", fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
    iconBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 },
  };

  return (
    <div style={{ animation: 'sIn 0.3s ease', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Daily Target Progress Bar (Admin Only) */}
      {role === 'admin' && (
        <div style={{ background: '#ffffff', borderRadius: 16, padding: 20, marginBottom: 24, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ color: '#0f172a', fontSize: 16, fontWeight: 700, margin: '0 0 4px 0' }}>🎯 هدف اليوم</h3>
              <p style={{ color: '#64748b', fontSize: 12, margin: 0 }}>{todayCount}/{todayTarget} مطعم جديد اليوم</p>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#4ecdc4', fontSize: 24, fontWeight: 900 }}>{Math.round(targetProgress)}%</div>
            </div>
          </div>
          <div style={{ height: 12, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <div style={{ 
              height: '100%',
              width: `${targetProgress}%`,
              background: targetProgress >= 100 ? 'linear-gradient(90deg, #10b981, #34d399)' : targetProgress >= 50 ? 'linear-gradient(90deg, #4ecdc4, #38b2ac)' : 'linear-gradient(90deg, #f97316, #fb923c)',
              borderRadius: 6,
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: '#000000' }}>
            {targetProgress >= 100 ? (
              <span style={{ color: '#10b981', fontWeight: 700 }}>✅ ممتاز! تم تحقيق الهدف</span>
            ) : targetProgress >= 50 ? (
              <span style={{ color: '#4ecdc4' }}>👍 عمل جيد! استمر</span>
            ) : (
              <span>💪 نحتاج {todayTarget - todayCount} مطاعم أخرى لتحقيق الهدف</span>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
            {viewMode === 'my-contacts' ? '📋 جهات اتصالي' : role === 'rep' ? 'مهامي الميدانية' : role === 'data_entry' ? 'قيد المراجعة' : role === 'accountant' ? 'بانتظار الفواتير' : 'جهات الاتصال'}
          </h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            {visibleRestaurants.length} سجل · {visibleRestaurants.filter(r => r.status === 'deal_closed').length} مغلق
            {userFilter && (
              <span style={{ marginRight: 8, color: '#7c3aed', fontWeight: 700 }}>
                👁️ عرض عمل: {userFilter.userName}
                <button onClick={() => setUserFilter(null)} style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="إزالة الفلتر">✕</button>
              </span>
            )}
          </p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canImport && <button onClick={() => setModal({ type: 'import_csv' })} style={S.btnSec}>📥 استيراد CSV</button>}
          {canAdd && <button onClick={() => setModal({ type: 'add_restaurant' })} style={role === 'marketer' ? S.btnPrimary : S.btnSec}>+ إضافة مطعم</button>}
          {role === 'admin' && <button onClick={() => setModal({ type: 'add_user' })} style={S.btnPrimary}>+ مستخدم</button>}
        </div>
      </div>

      {/* Pipeline stage counts */}
      <div className="pipeline-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 8, marginBottom: 20 }}>
        {Object.entries(STATUS_META).map(([k, v]) => (
          <div 
            key={k} 
            onClick={() => setStatusFilter(p => p === k ? 'all' : k)}
            style={{ 
              background: statusFilter === k ? v.bg : '#ffffff',
              borderRadius: 11, 
              padding: '12px 14px',
              border: `1px solid ${statusFilter === k ? v.color + '66' : '#e2e8f0'}`, 
              cursor: 'pointer', 
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <div style={{ fontSize: 10, color: v.color, fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }} className="pipeline-label">
              <Icon name={v.icon} size={12} color={v.color} />
              <span>{v.label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: v.color }} className="pipeline-count">{statusCounts[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <input 
          value={search} 
          onChange={e => setSearch(e.target.value)} 
          placeholder="🔍 بحث بالاسم أو الهاتف أو المنطقة..."
          style={{ ...S.input, flex: 1, minWidth: 220 }}
        />
        {selectedIds.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#f8fafc', borderRadius: 10, padding: '6px 14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <span style={{ color: '#000000', fontSize: 13 }}>تم تحديد {selectedIds.length}</span>
            {(role === 'admin' || role === 'sales_manager') && (
              <button onClick={() => setModal({ type: 'bulk_assign' })} style={{ ...S.btnPrimary, padding: '5px 12px', fontSize: 12 }}>تعيين جماعي</button>
            )}
            {(role === 'admin' || role === 'sales_manager') && (
              <button 
                onClick={handleBulkDelete} 
                disabled={deleting}
                style={{ 
                  background: '#dc2626', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '5px 12px', 
                  color: '#fff', 
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Cairo',sans-serif", 
                  fontSize: 12,
                  fontWeight: 700,
                  opacity: deleting ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {deleting ? '⏳ جارِ الحذف...' : '🗑️ حذف جماعي'}
              </button>
            )}
            {(role === 'admin' || role === 'sales_manager') && (
              <button 
                onClick={handleBulkArchive} 
                disabled={archiving}
                style={{ 
                  background: '#f59e0b', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '5px 12px', 
                  color: '#fff', 
                  cursor: archiving ? 'not-allowed' : 'pointer',
                  fontFamily: "'Cairo',sans-serif", 
                  fontSize: 12,
                  fontWeight: 700,
                  opacity: archiving ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {archiving ? '⏳ جارِ الأرشفة...' : '📦 أرشفة جماعية'}
              </button>
            )}
            <button onClick={() => setSelectedIds([])} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
        )}
      </div>

      {/* Admin/Marketer Filter Bar */}
      {(role === 'admin' || role === 'sales_manager' || role === 'marketer') && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          marginBottom: 16,
          padding: 16,
          background: '#ffffff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          {/* Area Filter */}
          <div>
            <label style={{ color: '#64748b', fontSize: 11, marginBottom: 4, display: 'block' }}>
              المنطقة
            </label>
            <select
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
              style={S.input}
            >
              <option value="all">كل المناطق</option>
              {uniqueAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>

          {/* Marketer Filter (Admin/Sales Manager only) */}
          {(role === 'admin' || role === 'sales_manager') && (
            <div>
              <label style={{ color: '#64748b', fontSize: 11, marginBottom: 4, display: 'block' }}>
                المنسّقة
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={marketerFilter}
                  onChange={(e) => setMarketerFilter(e.target.value)}
                  style={{ ...S.input, flex: 1 }}
                >
                  <option value="all">كل المنسّقات</option>
                  <option value="unassigned">⚪ غير معينة (عامة)</option>
                  {users
                    .filter(u => u.role === 'marketer')
                    .sort((a, b) => a.name.localeCompare(b.name, 'ar'))
                    .map(marketer => (
                      <option key={marketer.id} value={marketer.id}>
                        {marketer.name} ({visibleRestaurants.filter(r => r.assignedTo === marketer.id).length})
                      </option>
                    ))
                  }
                </select>
                {marketerFilter !== 'all' && marketerFilter !== 'unassigned' && (
                  <button
                    onClick={() => {
                      setReassignTargetMarketer(marketerFilter);
                      setShowMarketerReassign(true);
                    }}
                    style={{
                      padding: '6px 10px',
                      background: '#4ecdc4',
                      border: 'none',
                      borderRadius: 6,
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title="إعادة تعيين المنسّقة"
                  >
                    🔄
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Assignment Status Filter */}
          <div>
            <label style={{ color: '#64748b', fontSize: 11, marginBottom: 4, display: 'block' }}>
              حالة التعيين
            </label>
            <select
              value={assignmentFilter}
              onChange={(e) => setAssignmentFilter(e.target.value)}
              style={S.input}
            >
              <option value="all">الكل</option>
              <option value="unassigned">غير معين</option>
              <option value="assigned">معين</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label style={{ color: '#64748b', fontSize: 11, marginBottom: 4, display: 'block' }}>
              ترتيب حسب
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={S.input}
            >
              <option value="name-asc">الاسم (أ-ي)</option>
              <option value="name-desc">الاسم (ي-أ)</option>
              <option value="date-newest">الأحدث أولاً</option>
              <option value="date-oldest">الأقدم أولاً</option>
            </select>
          </div>

          {/* Page Size */}
          <div>
            <label style={{ color: '#64748b', fontSize: 11, marginBottom: 4, display: 'block' }}>
              عرض لكل صفحة
            </label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={S.input}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ ...S.th, width: 38 }}>
                  <CheckBox checked={allSelected} onChange={toggleAll} />
                </th>
                <th style={{ ...S.th, width: 44 }}>شعار</th>
                {['المطعم', 'المنطقة', 'الملفات', 'المرحلة',
                  ...(role === 'admin' || role === 'sales_manager' ? ['المنسّقة', 'المندوب'] : []),
                  'إجراء'
                ].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                    <div style={{ fontSize: 14, color: '#64748b' }}>لا توجد سجلات</div>
                  </td>
                </tr>
              )}
              {paginatedRestaurants.map(restaurant => (
                <LeadRow 
                  key={restaurant.id} 
                  lead={restaurant} 
                  ctx={ctx}
                  selected={selectedIds.includes(restaurant.id)} 
                  onToggle={() => toggleOne(restaurant.id)} 
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk Reassign Button */}
      {selectedIds.length > 0 && (role === 'admin' || role === 'sales_manager') && (
        <div style={{
          marginTop: 16,
          padding: 16,
          background: '#f5f3ff',
          borderRadius: 12,
          border: '1px solid #a78bfa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ color: '#a5b4fc', fontSize: 14, fontWeight: 700 }}>
            تم تحديد {selectedIds.length} مطعم
          </div>
          
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={reassignModal?.marketerId || ''}
              onChange={(e) => setReassignModal({ marketerId: e.target.value })}
              style={{
                ...S.input,
                width: 200,
              }}
            >
              <option value="">اختر المنسّقة</option>
              {users
                .filter(u => u.role === 'marketer')
                .map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))
              }
            </select>
            
            <button
              onClick={async () => {
                if (!reassignModal?.marketerId) {
                  ctx.showToast('يرجى اختيار المنسّقة', 'error');
                  return;
                }
                
                // Bulk update selected restaurants
                const { updateRestaurant } = await import('../services/restaurantService');
                const promises = selectedIds.map(id => 
                  updateRestaurant(id, { assignedTo: reassignModal.marketerId }, currentUser)
                );
                
                await Promise.all(promises);
                ctx.showToast(`✅ تم تعيين ${selectedIds.length} مطعم بنجاح`);
                setSelectedIds([]);
                setReassignModal(null);
                
                // Reload data
                ctx.loadRestaurants?.();
              }}
              style={S.btnPrimary}
            >
              تعيين للمنسّقة
            </button>
            
            <button
              onClick={() => {
                setSelectedIds([]);
                setReassignModal(null);
              }}
              style={S.btnSec}
            >
              إلغاء التحديد
            </button>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 16,
          padding: 16,
          background: '#ffffff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{ color: '#64748b', fontSize: 13 }}>
            عرض {(currentPage - 1) * pageSize + 1} إلى {Math.min(currentPage * pageSize, filtered.length)} من {filtered.length}
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                ...S.btnSec,
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              السابق
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  style={{
                    ...S.btnSec,
                    background: currentPage === pageNum ? '#4ecdc4' : 'transparent',
                    color: currentPage === pageNum ? '#fff' : '#4d6680',
                    minWidth: 40,
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                ...S.btnSec,
                opacity: currentPage === totalPages ? 0.5 : 1,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              التالي
            </button>
          </div>
        </div>
      )}

      {/* Marketer Reassign Modal */}
      {showMarketerReassign && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }}>
          <div style={{
            background: '#ffffff',
            border: '2px solid #4ecdc4',
            borderRadius: 16,
            padding: 32,
            maxWidth: 500,
            width: '90%',
            direction: 'rtl',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 16 }}>
              🔄 إعادة تعيين المنسّقة
            </h3>
            <p style={{ color: '#000000', fontSize: 14, marginBottom: 24 }}>
              سيتم نقل جميع مطاعم 
              <span style={{ color: '#60a5fa', fontWeight: 700 }}>
                {' '}{users.find(u => u.id === reassignTargetMarketer)?.name}{' '}
              </span>
              إلى منسّقة أخرى
            </p>

            <div style={{ marginBottom: 24 }}>
              <label style={{ color: '#000000', fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'block' }}>
                اختر المنسّقة الجديدة:
              </label>
              <select
                value={reassignTargetMarketer}
                onChange={(e) => setReassignTargetMarketer(e.target.value)}
                style={{
                  width: '100%',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#000000',
                  fontSize: 14,
                  fontFamily: "'Cairo',sans-serif",
                }}
              >
                <option value="">⚪ غير معينة (عامة)</option>
                {users
                  .filter(u => u.role === 'marketer' && u.id !== reassignTargetMarketer)
                  .sort((a, b) => a.name.localeCompare(b.name, 'ar'))
                  .map(marketer => (
                    <option key={marketer.id} value={marketer.id}>
                      {marketer.name}
                    </option>
                  ))
                }
              </select>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={async () => {
                  if (!reassignTargetMarketer && reassignTargetMarketer !== '') {
                    showToast('⚠️ يرجى اختيار منسّقة', 'warning');
                    return;
                  }
                  
                  try {
                    const newAssignedTo = reassignTargetMarketer || null;
                    const filteredRestaurants = visibleRestaurants.filter(r => 
                      marketerFilter === 'unassigned' ? !r.assignedTo : r.assignedTo === marketerFilter
                    );
                    
                    for (const restaurant of filteredRestaurants) {
                      await ctx.updateRestaurant(restaurant.id, { assignedTo: newAssignedTo });
                    }
                    
                    showToast(`✅ تم إعادة تعيين ${filteredRestaurants.length} مطعم بنجاح`);
                    setShowMarketerReassign(false);
                    setReassignTargetMarketer('');
                    setMarketerFilter('all');
                  } catch (err) {
                    console.error('Reassign error:', err);
                    showToast('❌ فشل في إعادة التعيين', 'error');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                تأكيد إعادة التعيين
              </button>
              <button
                onClick={() => {
                  setShowMarketerReassign(false);
                  setReassignTargetMarketer('');
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  color: '#000000',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Feedback Modal (Blocks new claims until submitted) */}
      {feedbackModal && feedbackModal.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
        }}>
          <div style={{
            background: '#ffffff',
            border: '2px solid #f59e0b',
            borderRadius: 16,
            padding: 32,
            maxWidth: 600,
            maxHeight: '90vh',
            width: '90%',
            overflowY: 'auto',
            direction: 'rtl',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f59e0b22', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <span style={{ fontSize: 32 }}>⚠️</span>
              </div>
              <h3 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
                تنبيه: مطلوب سبب التأخير
              </h3>
              <p style={{ color: '#000000', fontSize: 14 }}>
                لديك {feedbackModal.length} مطعم/مطاعم تم العمل عليها لأكثر من {FEEDBACK_HOURS_THRESHOLD} ساعة بدون إتمام التسجيل
              </p>
            </div>

            <div style={{
              padding: 16,
              background: '#ef444411',
              borderRadius: 8,
              border: '1px solid #ef444444',
              marginBottom: 20,
            }}>
              <p style={{ color: '#f87171', fontSize: 13, margin: 0, fontWeight: 700 }}>
                🚫 لن تتمكن من حجز مطاعم جديدة حتى تقدم سبب التأخير لهذه المطاعم
              </p>
            </div>

            {/* List of pending feedback items */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                المطاعم المطلوب تقديم سبب لها:
              </h4>
              {feedbackModal.map((item, idx) => (
                <PendingFeedbackItem
                  key={item.id}
                  item={item}
                  onSubmit={async (reasonCategory, reasonText) => {
                    setFeedbackSubmitting(true);
                    const result = await submitFeedback(item.id, reasonCategory, reasonText);
                    setFeedbackSubmitting(false);
                    
                    if (result.success) {
                      ctx.showToast?.('✅ تم تقديم السبب وتحرير المطعم');
                      // Remove from list
                      setFeedbackModal(prev => prev.filter(x => x.id !== item.id));
                      
                      // Check if all submitted
                      if (feedbackModal.length === 1) {
                        setFeedbackModal(null);
                      }
                    } else {
                      ctx.showToast?.('❌ فشل في تقديم السبب', 'error');
                    }
                  }}
                  submitting={feedbackSubmitting}
                />
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
function LeadRow({ lead: l, ctx, selected, onToggle }) {
  const { users, currentUser, setModal, settings } = ctx;
  const role = currentUser.role;
  const st = STATUS_META[l.status];
  const marketer = users.find(u => u.id === l.assignedTo);
  const rep = users.find(u => u.id === l.assignedRep);
  const filesCount = Object.values(l.files || {}).filter(Boolean).length;
  const totalFiles = 6;

  const waPhone = (l.phone || '').replace(/\D/g, '');
  const waMsg = encodeURIComponent(settings?.whatsappMsg || '');

  // Row background color based on claim status
  const getRowStyle = () => {
    if (l.wrongInfoFlag && l.wrongInfoStatus === 'pending') {
      return { bg: '#fff7ed', hover: '#ffedd5', opacity: 0.6 };
    }
    // Available - light tint
    return { bg: 'transparent', hover: '#f0fdfa', opacity: 1 };
  };
  const rowStyle = getRowStyle();

  // Which action modal to open
  const getAction = () => {
    if (role === 'marketer' && l.status === 'new') return { type: 'onboarding', data: l };
    if (role === 'data_entry' && l.status === 'data_review') return { type: 'dataentry_action', data: l };
    if ((role === 'sales_manager' || role === 'admin') && (l.status === 'ready_for_rep' || l.status === 'pending_closure')) return { type: 'manager_action', data: l };
    if (role === 'accountant' && l.status === 'invoice_pending') return { type: 'accountant_action', data: l };
    if (role === 'rep' && (l.status === 'in_field' || l.status === 'pending_closure')) return { type: 'rep_action', data: l };
    return null;
  };

  const actionModal = getAction();

  // Determine status badges to show
  const getStatusBadges = () => {
    const badges = [];
    
    // Wrong Info Under Review badge
    if (l.wrongInfoFlag && l.wrongInfoStatus === 'pending') {
      badges.push({ label: 'بانتظار المراجعة', color: '#f59e0b', bg: '#ffedd5', icon: 'AlertCircle' });
    }
    
    // Not Interested badge (check notes)
    if (l.notes?.includes('غير مهتم')) {
      badges.push({ label: 'غير مهتم', color: '#ef4444', bg: '#fef2f2', icon: '❌' });
    }
    
    // Scheduled Call badge
    if (l.scheduled_call_at) {
      const scheduledDate = new Date(l.scheduled_call_at);
      const now = new Date();
      const isFuture = scheduledDate > now;
      
      badges.push({ 
        label: isFuture ? 'مجدول' : 'تم الاتصال', 
        color: isFuture ? '#f59e0b' : '#10b981', 
        bg: isFuture ? '#fef3c7' : '#d1fae5', 
        icon: '📅',
        tooltip: scheduledDate.toLocaleString('ar-AE', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      });
    }
    
    // Edited badge (if updated_at is recent - within last 24 hours)
    if (l.updated_at) {
      const updatedAt = new Date(l.updated_at);
      const hoursAgo = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60);
      
      if (hoursAgo < 24) {
        badges.push({ label: 'معدّل', color: '#4ecdc4', bg: '#f0fdfa', icon: '✏️' });
      }
    }
    
    return badges;
  };

  const statusBadges = getStatusBadges();

  return (
    <tr 
      style={{ 
        borderBottom: '1px solid #e2e8f0',
        background: rowStyle.bg,
        opacity: rowStyle.opacity,
      }}
      onMouseEnter={e => e.currentTarget.style.background = rowStyle.hover}
      onMouseLeave={e => e.currentTarget.style.background = rowStyle.bg}
    >
      <td style={{ padding: '10px 14px' }}><CheckBox checked={selected} onChange={onToggle} /></td>
      <td style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {/* Status Badges */}
          {statusBadges.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 4 }}>
              {statusBadges.map((badge, idx) => (
                <div 
                  key={idx}
                  title={badge.tooltip || badge.label}
                  style={{ 
                    padding: '2px 6px', 
                    background: badge.bg, 
                    border: `1px solid ${badge.color}44`,
                    borderRadius: 4, 
                    fontSize: 9,
                    fontWeight: 700,
                    color: badge.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    whiteSpace: 'nowrap',
                    cursor: badge.tooltip ? 'help' : 'default',
                  }}
                >
                  <Icon name={badge.icon} size={12} color={badge.color} />
                  <span>{badge.label}</span>
                </div>
              ))}
            </div>
          )}
          {/* Logo */}
          {l.files?.logoUrl
            ? <img src={l.files.logoUrl} alt="" style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', border: '1px solid #e2e8f0' }} onError={e => { e.target.src = 'https://placehold.co/38/1a2540/475569?text=🍽'; }} />
            : <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f1f5f9', border: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🍽️</div>
          }
        </div>
      </td>
      <td style={{ padding: '10px 14px' }}>
        <button 
          onClick={() => {
            // Open restaurant details directly
            setModal({ type: 'view_details', data: l });
          }} 
          style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'right', padding: 0 }}
        >
          <div style={{ color: '#000000', fontWeight: 700, fontSize: 14 }}>{l.name}</div>
          {l.returnNote && <div style={{ color: '#f97316', fontSize: 10, marginTop: 2 }}>⚠ تم الإرجاع</div>}
        </button>
      </td>
      <td style={{ padding: '10px 14px', color: '#4d6680', fontSize: 12 }}>{l.area}</td>
      <td style={{ padding: '10px 14px', minWidth: 80 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${(filesCount / totalFiles) * 100}%`, height: '100%', background: filesCount === totalFiles ? '#34d399' : '#f59e0b', borderRadius: 2 }} />
          </div>
          <span style={{ fontSize: 10, color: filesCount === totalFiles ? '#34d399' : '#f59e0b', whiteSpace: 'nowrap' }}>{filesCount}/{totalFiles}</span>
        </div>
      </td>
      <td style={{ padding: '10px 14px' }}>
        <StatusBadge status={l.status} />
      </td>
      {(role === 'admin' || role === 'sales_manager') && <>
        <td style={{ padding: '10px 14px', fontSize: 12, color: '#3d5470' }}>{marketer?.name || '—'}</td>
        <td style={{ padding: '10px 14px', fontSize: 12, color: rep ? '#34d399' : '#3d5470' }}>{rep?.name || '—'}</td>
      </>}
      <td style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {/* Quick WhatsApp Button */}
          {l.phone && (
            <a
              href={`https://wa.me/${waPhone}?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              title="واتساب"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 7,
                background: '#25D36618',
                border: '1px solid #25D36644',
                cursor: 'pointer',
                fontSize: 14,
                textDecoration: 'none',
                transition: 'all 0.15s',
                flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#25D36633'}
              onMouseLeave={e => e.currentTarget.style.background = '#25D36618'}
            >
              💬
            </a>
          )}
          
          {/* Quick Google Maps Button */}
          {(l.mapsUrl || l.latitude || l.longitude || l.lat || l.lng) && (
            <a
              href={l.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${l.latitude || l.lat},${l.longitude || l.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              title="الموقع على الخريطة"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 7,
                background: '#4285F418',
                border: '1px solid #4285F444',
                cursor: 'pointer',
                fontSize: 14,
                textDecoration: 'none',
                transition: 'all 0.15s',
                flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#4285F433'}
              onMouseLeave={e => e.currentTarget.style.background = '#4285F418'}
            >
              📍
            </a>
          )}
          
          {actionModal && role !== 'marketer' && (
            <SmBtn color={st?.color || '#4ecdc4'} onClick={() => setModal(actionModal)}>
              {role === 'data_entry' ? '📤 إرسال للمراجعة'
              : role === 'accountant' ? '💰 مراجعة'
              : (role === 'sales_manager' || role === 'admin') && l.status === 'ready_for_rep' ? '🔍 مراجعة'
              : (role === 'sales_manager' || role === 'admin') && l.status === 'pending_closure' ? '✅ إغلاق'
              : role === 'rep' ? '📋 مراجعة'
              : 'فتح'}
            </SmBtn>
          )}
          {actionModal && role === 'marketer' && l.status !== 'new' && (
            <SmBtn color={st?.color || '#4ecdc4'} onClick={() => setModal(actionModal)}>
              📤 رفع ملفات
            </SmBtn>
          )}
          {(role === 'admin' || role === 'sales_manager') && (
            <SmBtn color="#ef4444" onClick={() => handleDeleteRestaurant(l)}>🗑️</SmBtn>
          )}
          {(role === 'admin' || role === 'sales_manager') && l.status === 'deal_closed' && (
            <SmBtn color="#f59e0b" onClick={() => handleArchiveRestaurant(l)}>📦</SmBtn>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── HELPER COMPONENTS ────────────────────────────────────────────────
function CheckBox({ checked, onChange }) {
  return (
    <div onClick={onChange} style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${checked ? '#4ecdc4' : '#e2e8f0'}`, background: checked ? '#4ecdc4' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </div>
  );
}

function SmBtn({ color, onClick, children, style: extraStyle }) {
  return (
    <button onClick={onClick} style={{ background: color + '18', border: `1px solid ${color}44`, borderRadius: 7, color, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontFamily: "'Cairo',sans-serif", fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s', ...extraStyle }}
      onMouseEnter={e => e.currentTarget.style.background = color + '33'}
      onMouseLeave={e => e.currentTarget.style.background = color + '18'}>
      {children}
    </button>
  );
}

function PendingFeedbackItem({ item, onSubmit, submitting }) {
  const [selectedReason, setSelectedReason] = useState('');
  const [customText, setCustomText] = useState('');
  const [expanded, setExpanded] = useState(false);
  
  const restaurant = item.restaurants;
  const hoursWorked = Math.round(item.hours_worked);
  
  const handleSubmit = () => {
    if (!selectedReason) return;
    onSubmit(selectedReason, customText || FEEDBACK_REASONS.find(r => r.value === selectedReason)?.label || '');
  };
  
  return (
    <div style={{
      background: '#e2e8f0',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      marginBottom: 12,
      overflow: 'hidden',
    }}>
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            🍽️ {restaurant?.name || 'مطعم'}
          </div>
          <div style={{ color: '#64748b', fontSize: 12 }}>
            ⏱️ {hoursWorked} ساعة عمل • {restaurant?.area || '—'}
          </div>
        </div>
        <span style={{ color: '#64748b', fontSize: 18 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>
      
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #e2e8f0' }}>
          <label style={{ color: '#000000', fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 8 }}>
            سبب التأخير / التجميد: *
          </label>
          <select
            value={selectedReason}
            onChange={e => setSelectedReason(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              color: '#000000',
              fontSize: 13,
              fontFamily: "'Cairo', sans-serif",
              marginBottom: 12,
            }}
          >
            <option value="">اختر السبب...</option>
            {FEEDBACK_REASONS.map(reason => (
              <option key={reason.value} value={reason.value}>{reason.label}</option>
            ))}
          </select>
          
          <label style={{ color: '#000000', fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 8 }}>
            ملاحظات إضافية (اختياري):
          </label>
          <textarea
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            placeholder="اكتب أي تفاصيل إضافية..."
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              color: '#000000',
              fontSize: 13,
              fontFamily: "'Cairo', sans-serif",
              resize: 'vertical',
              marginBottom: 12,
            }}
          />
          
          <button
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            style={{
              width: '100%',
              padding: '10px 16px',
              background: selectedReason && !submitting ? 'linear-gradient(135deg, #10b981, #059669)' : '#ffffff',
              border: 'none',
              borderRadius: 6,
              color: selectedReason && !submitting ? '#fff' : '#64748b',
              fontSize: 14,
              fontWeight: 700,
              cursor: selectedReason && !submitting ? 'pointer' : 'not-allowed',
              fontFamily: "'Cairo', sans-serif",
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'جاري الإرسال...' : '✅ تقديم السبب وتحرير المطعم'}
          </button>
        </div>
      )}
    </div>
  );
}

// Simple styles object
const S = {
  iconBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, background: '#ffffff', border: '1px solid #e2e8f0', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 },
};
